import { createApi, retry } from "@reduxjs/toolkit/query/react";
import { createFlatBufferBaseQuery } from "../../flatbuffers/api/flatBufferBaseQuery";
import fetchAllPages from "../../utils/fetchAllPages";

const maxRetries = 2;

const baseQuery = retry(
  createFlatBufferBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? ""),
  { maxRetries },
);

export const serverAPI = createApi({
  reducerPath: "serverAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: [
    "tagGetServers",
    "tagGetServersLight",
    "tagPostServer",
    "tagPutServer",
    "tagDeleteServer",
    "tagGetSecretClientToken",
    "tagGetEdgeProvisionConfig",
  ],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL SERVERS */
    getServers: builder.query({
      query: (body: any) => ({
        url: `/servers/?sort=name&sort_direction=asc&page=${body.page}&limit=${body.limit}${
          body.searchAndFilterString || ""
        }`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* GET ALL SERVERS LIGHT */
    getServersLight: builder.query({
      query: (body: any) => ({
        url: `/servers/light/${body.orgId ?? ""}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServersLight"],
    }),
    //* *********************************************************** */
    //* GET ONE SERVERS */
    getServer: builder.query({
      query: (body: any) => ({
        url: `/servers/${body.idServer}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* GET SERVERS BY ORGANIZATION */
    getServersByOrganization: builder.query({
      queryFn: async (body: any, _queryApi, _extraOptions, _baseQuery) =>
        fetchAllPages(
          _baseQuery,
          `/servers/?filter_target=org_id&filter=${body.orgId}&page=1&limit=500`,
          { authorization: body.token },
        ),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* POST SECRET CLIENT TOKEN */
    postSecretClientToken: builder.mutation({
      query: (body: any) => ({
        url: "/servers/oauth",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* CREATE SERVER */
    postServer: builder.mutation({
      query: (body: any) => ({
        url: "/servers/",
        method: "POST",
        body: body.body,
        headers: { authorization: body.token },
      }),
      extraOptions: { fbSerializer: "CreateServer" },
      invalidatesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* EDIT SERVER */
    putServer: builder.mutation({
      queryFn: async (body: any, _queryApi, _extraOptions, fetchWithBQ) => {
        if (!body.body.id) {
          return {
            error: {
              status: 400,
              data: { message: "Server ID is required for update" },
            },
          };
        }
        return fetchWithBQ({
          url: `/servers/${body.body.id}`,
          method: "PUT",
          body: body.body,
          headers: { authorization: body.token },
        });
      },
      extraOptions: { fbSerializer: "UpdateServer" },
      invalidatesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* DELETE SERVER */
    deleteServer: builder.mutation({
      queryFn: async (body: any, _queryApi, _extraOptions, fetchWithBQ) => {
        if (!body.id) {
          return {
            error: {
              status: 400,
              data: { message: "Server ID is required for delete" },
            },
          };
        }
        return fetchWithBQ({
          url: `/servers/${body.id}`,
          method: "DELETE",
          headers: { authorization: body.token },
        });
      },
      invalidatesTags: ["tagGetServers"],
    }),

    //* *********************************************************** */
    //* EXPORT SERVER INFO*/
    getExportServerInfo: builder.query({
      query: (body: any) => ({
        url: `/servers/export/${body.serverId}`,
        headers: { authorization: body.token },
        responseHandler: async (response) => {
          if (!response.ok) throw new Error("Error retrieving the file.");
          const blob = await response.blob();
          const contentDisposition = response.headers.get("Content-Disposition") || "";
          return { blob, contentDisposition };
        },
      }),
      providesTags: ["tagGetServers"],
    }),
    //* *********************************************************** */
    //* EDGE PROVISIONING - GET CONFIG BY MAC */
    getEdgeProvisionConfig: builder.query({
      query: (body: any) => ({
        url: `/edge/config/${body.mac}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetEdgeProvisionConfig"],
    }),
    //* *********************************************************** */
    //* EDGE PROVISIONING - UPDATE STATUS */
    patchEdgeProvisionStatus: builder.mutation({
      query: (body: any) => ({
        url: `/edge/status/${body.serverId}`,
        method: "PATCH",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetServers"],
    }),
  }),
});

export const {
  useLazyGetServersQuery,
  useGetServersLightQuery,
  useLazyGetServerQuery,
  useLazyGetServersByOrganizationQuery,
  usePostServerMutation,
  usePostSecretClientTokenMutation,
  usePutServerMutation,
  useDeleteServerMutation,
  useLazyGetExportServerInfoQuery,
  useLazyGetEdgeProvisionConfigQuery,
  usePatchEdgeProvisionStatusMutation,
} = serverAPI;
